// src/pages/Eleve.jsx
import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  getDocs,
  setDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { useNavigate, useLocation } from "react-router-dom";
// Arrière-plan Espace élève
import eleveBg from "../assets/horloge-et-papiers-avec-fond-brillant.jpg";
import PreviewPanel from "../components/PreviewPanel";
import { titleOf, extOf, r2UrlFromKey } from "../utils/fileUtils";

// Configurer le worker pdf.js (CDN)
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/* ----------- Utilitaires ----------- */
const SUBJECTS = [
  { code: "PHILO", label: "Philosophie",     emoji: "🏛️" },
  { code: "ANG",   label: "Anglais",         emoji: "🇬🇧" },
  { code: "MATH",  label: "Maths",           emoji: "➗"  },
  { code: "PC",    label: "Physique-Chimie", emoji: "⚛️" },
  { code: "SVT",   label: "SVT",             emoji: "🧬"  },
];

// Matières spécifiques pour l’onglet 3AC Fr
const SUBJECTS_3AC_FR = [
  { code: "PC",   label: "Physique-Chimie",       emoji: "⚛️" },
  { code: "SVT",  label: "SVT",                   emoji: "🧬" },
  { code: "MATH", label: "Maths",                 emoji: "➗" },
  { code: "HG",   label: "Histoire-Géographie",   emoji: "🗺️" },
  { code: "EI",   label: "Éducation islamique",   emoji: "🕌" },
  { code: "FR",   label: "Français",              emoji: "🇫🇷" },
  { code: "AR",   label: "Arabe",                 emoji: "🇲🇦" },
];

// Valeurs de scope considérées comme 3AC (pour compatibilité)
const SCOPE_3AC_VALUES = [
  "3AC-Fr", "3AC-Fr", "3ac-fr", "3acfr", "3ACFr", "3AC FR", "3ACfr", "3AC fr", "3AC", "3ac"
];

function sortPrefId(ownerUid, folderId) {
  return `${ownerUid}__${folderId || "root"}`;
}

async function verifyReferralCode(db, rawCode) {
  if (!rawCode) return { ok: true, code: null, profId: null, discount: 0 };
  const id = rawCode.trim().toUpperCase();
  const snap = await getDoc(doc(db, "refCodes", id));
  if (!snap.exists()) return { ok: false, error: "Code invalide." };
  const data = snap.data();
  if (data.active === false) return { ok: false, error: "Code désactivé." };
  return { ok: true, code: id, profId: data.profId || null, discount: Number(data.discount ?? 50) };
}

function sanitizeFileName(name) {
  try {
    return name
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w.-]+/g, "_")
      .replace(/_+/g, "_")
      .slice(0, 120);
  } catch {
    return String(name || "fichier").replace(/\s+/g, "_").slice(0, 120);
  }
}

/* ----------- RIB à afficher avant le dépôt du reçu ----------- */
const SCHOOL_RIB = "230 815 6715228211015100 30";

export default function Eleve() {
  const videoRef = useRef(null);
  const auth = getAuth();
  const storage = getStorage();
  const navigate = useNavigate();
  const location = useLocation();

  // Déduire la section (3AC-Fr vs 2Bac) à partir des paramètres de l'URL
  const params = new URLSearchParams(location.search || "");
  const is3AC = params.get("section") === "3AC-Fr";
  const isDebug = params.get("debug") === "1";

  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  // Arborescence
  const [teacherUids, setTeacherUids] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [crumbs, setCrumbs] = useState([{ id: null, name: "Racine" }]);
  const [folders, setFolders] = useState([]);

  // Fichiers
  const [contents, setContents] = useState([]);
  const urlCache = useRef(new Map());

  // Lecteur
  const [preview, setPreview] = useState(null);
  const [embedError, setEmbedError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Référence pour le scroll automatique
  const previewRef = useRef(null);

  // Onboarding (niveau + code + reçu)
  const [levelChoice, setLevelChoice] = useState("2bac");
  const [savingLevel, setSavingLevel] = useState(false);
  const [levelSavedMsg, setLevelSavedMsg] = useState("");
  const refCodeRef = useRef(null);
  const [refAlert, setRefAlert] = useState(null);
  const fileInputRef = useRef(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  // Étape 3 — Téléphone et consentement
  const phoneInputRef = useRef(null);
  const [phoneConsent, setPhoneConsent] = useState(true);
  const [savingPhone, setSavingPhone] = useState(false);
  const [savePhoneMsg, setSavePhoneMsg] = useState("");

  // Prix
  const priceWithCode = 550;
  const priceFull     = 600;
  const hasAppliedCode = !!me?.referralCode;
  const priceToPay = hasAppliedCode ? priceWithCode : priceFull;

  // Dossier courant (restriction + tri partagé)
  const [currentFolder, setCurrentFolder] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [blockedMsg, setBlockedMsg] = useState("Pas pour le moment");
  const [ownerUidForSort, setOwnerUidForSort] = useState(null);
  
  // Tri personnalisé par l'élève
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // Bouton copier RIB
  const [ribCopied, setRibCopied] = useState(false);

  // Styles pour l'affichage compact
  const compactCardStyle = {
    minHeight: '80px',
  };

  const compactImageStyle = {
    fontSize: '20px',
  };

  /* ---------- Gestion de l'affichage des modes de paiement ---------- */
  useEffect(() => {
    // Gérer l'affichage des sections de paiement
    const handlePaymentMethodChange = () => {
      const bankRadio = document.getElementById('paymentBank');
      const cashRadio = document.getElementById('paymentCash');
      const ribSection = document.getElementById('ribSection');
      const cashSection = document.getElementById('cashSection');

      if (bankRadio && bankRadio.checked) {
        ribSection?.classList.remove('d-none');
        cashSection?.classList.add('d-none');
      } else if (cashRadio && cashRadio.checked) {
        ribSection?.classList.add('d-none');
        cashSection?.classList.remove('d-none');
      }
    };

    // Ajouter les écouteurs d'événements
    const bankRadio = document.getElementById('paymentBank');
    const cashRadio = document.getElementById('paymentCash');
    
    bankRadio?.addEventListener('change', handlePaymentMethodChange);
    cashRadio?.addEventListener('change', handlePaymentMethodChange);

    // Initialiser l'affichage
    handlePaymentMethodChange();

    return () => {
      bankRadio?.removeEventListener('change', handlePaymentMethodChange);
      cashRadio?.removeEventListener('change', handlePaymentMethodChange);
    };
  }, []);

  /* ---------- Redirection si l'élève est sur une autre route ---------- */
  useEffect(() => {
    const u = auth.currentUser;
    if (u && me && me.role === "eleve" && !location.pathname.startsWith("/eleve")) {
      navigate("/eleve", { replace: true });
    }
  }, [me, location, navigate, auth]);

  /* ---------- Scroll automatique vers la vidéo ---------- */
  useEffect(() => {
    if (preview && previewRef.current) {
      previewRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [preview]);

  /* ---------- Profil ---------- */
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    const unsub = onSnapshot(
      doc(db, "users", u.uid),
      (snap) => {
        if (snap.exists()) {
          const userData = { id: snap.id, ...snap.data() };
          setMe(userData);
          
          // Rediriger si l'utilisateur est un élève mais n'est pas sur /eleve
          if (userData.role === "eleve" && !location.pathname.startsWith("/eleve")) {
            navigate("/eleve", { replace: true });
          }
        } else {
          setMe(null);
        }
      },
      (err) => {
        console.error("users doc listener error (Eleve)", err);
        setMe(null);
      }
    );
    return () => unsub && unsub();
  }, [auth, location, navigate]);

  /* ---------- Profs pour la matière ---------- */
  useEffect(() => {
    if (!selected) return;
    setCurrentFolderId(null);
    setCrumbs([{ id: null, name: "Racine" }]);
    setFolders([]);
    setContents([]);
    setError("");
    setPreview(null);
    setEmbedError(false);

    (async () => {
      const s1 = await getDocs(query(collection(db, "users"), where("role","==","prof")));
      const s2 = await getDocs(query(collection(db, "users"), where("role","==","admin")));
      const code = selected.code.toLowerCase();
      const labelLower = selected.label.toLowerCase();

      const uids = [...s1.docs, ...s2.docs]
        .filter(d => {
          const data = d.data();
          const subj = (data.subject || "").toString().toLowerCase();
          const mat  = (data.matiere || "").toString().toLowerCase();
          return (
            subj === code || mat === code ||
            subj === labelLower || mat === labelLower ||
            subj.includes(code) || mat.includes(code) ||
            subj.includes(labelLower) || mat.includes(labelLower)
          );
        })
        .map(d => d.id);

      // Éviter d'écouter si aucun prof n'est détecté
      setTeacherUids(uids.length ? uids : []);
    })();
  }, [selected]);

  /* ---------- Fil d'Ariane ---------- */
  useEffect(() => {
    if (!selected) return;
    (async () => {
      if (!currentFolderId) {
        setCrumbs([{ id: null, name: "Racine" }]);
        return;
      }
      const trail = [];
      let nodeId = currentFolderId;
      while (nodeId) {
        const s = await getDoc(doc(db, "folders", nodeId));
        if (!s.exists()) break;
        const d = s.data();
        trail.push({ id: s.id, name: d.name, parentId: d.parentId || null });
        nodeId = d.parentId || null;
      }
      trail.reverse();
      setCrumbs([{ id: null, name: "Racine" }, ...trail]);
    })();
  }, [currentFolderId, selected]);

  /* ---------- Infos du dossier ---------- */
  useEffect(() => {
    (async () => {
      if (!currentFolderId) {
        setCurrentFolder(null);
        setBlocked(false);
        setBlockedMsg("Pas pour le moment");
        setOwnerUidForSort(null);
        return;
      }
      const s = await getDoc(doc(db, "folders", currentFolderId));
      if (!s.exists()) {
        setCurrentFolder(null);
        setBlocked(false);
        setOwnerUidForSort(null);
        return;
      }
      const d = s.data();
      setCurrentFolder({ id: s.id, ...d });
      setBlocked(!!d.restricted);
      setBlockedMsg(d.restrictedMsg || "Pas pour le moment");
      setOwnerUidForSort(d.ownerUid || null);
    })();
  }, [currentFolderId]);

  /* ---------- Écouter le tri sauvegardé par le prof ---------- */
  useEffect(() => {
    if (!ownerUidForSort) return;
    const prefId = sortPrefId(ownerUidForSort, currentFolderId || null);
    const dref = doc(db, "sortPrefs", prefId);
    const unsub = onSnapshot(
      dref,
      (snap) => {
        if (snap.exists()) {
          const x = snap.data();
          setSortKey(x.sortKey || "name");
          setSortDir(x.sortDir || "asc");
        } else {
          setSortKey("name");
          setSortDir("asc");
        }
      },
      (err) => {
        console.error("sortPrefs listener error", err);
        // Revenir à un tri sûr par défaut
        setSortKey("name");
        setSortDir("asc");
      }
    );
    return () => unsub();
  }, [ownerUidForSort, currentFolderId]);

  /* ---------- Dossiers visibles (les verrouillés restent visibles) ---------- */
  useEffect(() => {
    if (!selected || teacherUids.length === 0) return;

    const owners = teacherUids.slice(0, 20);
    const unsubs = [];
    const latestById = new Map();

    const emit = () => {
      const arr = Array.from(latestById.values())
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setFolders(arr);
    };

    owners.forEach((uid) => {
      try {
        const q = query(collection(db, "folders"), where("ownerUid", "==", uid));
        const u = onSnapshot(
          q,
          (snap) => {
            snap.docs.forEach((d) => {
              const data = d.data();
              const parent = data.parentId ?? null;
              // Exclure les dossiers marqués comme gratuits du côté élève
              if (data.free === true) { latestById.delete(d.id); return; }
              // Filtrage par section: afficher uniquement les dossiers pertinents
              const scopeVal = String(data.scope || "");
              const is3ACFolder = SCOPE_3AC_VALUES.includes(scopeVal);
              if (is3AC) {
                // En 3AC Fr, ne montrer que les dossiers marqués "3AC-Fr" (ou variantes)
                if (!is3ACFolder) { latestById.delete(d.id); return; }
              } else {
                // En 2Bac, masquer les dossiers marqués "3AC-Fr" (ou variantes)
                if (is3ACFolder) { latestById.delete(d.id); return; }
              }

              if ((currentFolderId ?? null) === parent) latestById.set(d.id, { id: d.id, ...data });
              else latestById.delete(d.id);
            });
            emit();
          },
          (err) => { console.error("folders listener error", err); setError("Erreur dossiers : " + err.message); }
        );
        unsubs.push(u);
      } catch (e) { console.error(e); }
    });

    return () => unsubs.forEach((u) => u && u());
  }, [selected, teacherUids, currentFolderId]);

  /* ---------- Fichiers du dossier (respecte le blocage + tri partagé) ---------- */
  useEffect(() => {
    if (!selected) return;

    /* désactivé: ne pas bloquer le listing */ if (false && blocked) {
      setLoading(false);
      setContents([]);
      return;
    }

    setLoading(true);
    setError("");
    setEmbedError(false);

    const colContents = collection(db, "contents");
    // Pour 3AC Fr: on filtre explicitement scope=="3AC-Fr".
    // Pour 2Bac: on NE filtre PAS par scope pour inclure les contenus anciens (scope absent ou null),
    // puis on exclut côté client ceux marqués "3AC-Fr".
    const q1  = is3AC
      ? query(colContents, where("subject", "==", selected.code), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("subject", "==", selected.code));
    const q1b = is3AC
      ? query(colContents, where("subject", "==", selected.code.toLowerCase()), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("subject", "==", selected.code.toLowerCase()));
    // Ajout: requêtes par libellé humain (ex: "Français", "Éducation islamique") pour matcher les contenus existants
    const label = selected.label;
    // Synonymes connus pour assurer la compatibilité entre "Maths" et "Mathématiques"
    const altNames = [];
    const lcLabel = String(label || "").toLowerCase();
    if (selected.code === "MATH" || lcLabel.includes("math")) {
      altNames.push("Mathématiques", "mathématiques", "Math", "math");
    }
    const q1c = is3AC
      ? query(colContents, where("subject", "==", label), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("subject", "==", label));
    const q1d = is3AC
      ? query(colContents, where("subject", "==", label.toLowerCase()), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("subject", "==", label.toLowerCase()));

    const q2  = is3AC
      ? query(colContents, where("matiere", "==", selected.code), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("matiere", "==", selected.code));
    const q2b = is3AC
      ? query(colContents, where("matiere", "==", selected.code.toLowerCase()), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("matiere", "==", selected.code.toLowerCase()));
    const q2c = is3AC
      ? query(colContents, where("matiere", "==", label), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("matiere", "==", label));
    const q2d = is3AC
      ? query(colContents, where("matiere", "==", label.toLowerCase()), where("scope", "==", "3AC-Fr"))
      : query(colContents, where("matiere", "==", label.toLowerCase()));

    // Capsules historiques (Firebase Storage) : désactivées pour forcer 100% Cloudflare R2
    const colCaps = collection(db, "capsules");
    const q3  = is3AC ? query(colContents, where("scope", "==", "3AC-Fr")) : null;
    const scopeAltValues = SCOPE_3AC_VALUES.filter(v => v !== "3AC-Fr");
    const q3b = is3AC ? query(colContents, where("scope", "in", scopeAltValues)) : null;

    // Requête spécifique au dossier courant (ignorer le sujet si on est dans le dossier)
    const qFolder = currentFolderId ? query(colContents, where("folderId", "==", currentFolderId)) : null;

    let s1 = [], s1b = [], s1c = [], s1d = [], s2 = [], s2b = [], s2c = [], s2d = [], s3 = [], s3b = [];
    let sFolder = [];
    // Ajouts pour les synonymes (ex: Mathématiques)
    const s1e = new Map();
    const s2e = new Map();

    const matchesFolder = (c) => {
      const fid = c.folderId ?? null;
      if (!currentFolderId) return fid === null || fid === "root" || typeof fid === "undefined";
      return fid === currentFolderId;
    };

    const mergeAndSet = () => {
      const map = new Map();
      const flatS1e = Array.from(s1e.values()).flat();
      const flatS2e = Array.from(s2e.values()).flat();
      // Inclure sFolder dans la fusion
      const allDocs = [...s1, ...s1b, ...s1c, ...s1d, ...s2, ...s2b, ...s2c, ...s2d, ...s3, ...s3b, ...flatS1e, ...flatS2e, ...sFolder];
      if (isDebug) console.log("mergeAndSet total docs:", allDocs.length, "sFolder count:", sFolder.length);

      for (const d of allDocs) {
        const c = d.data();
        if (c.published === false) continue;

        // Vérifier d'abord si l'élément appartient au dossier courant
        if (!matchesFolder(c)) continue;

        // Si nous sommes dans un dossier spécifique (pas à la racine), on affiche tout le contenu du dossier
        // peu importe les tags (free, scope...) car le dossier lui-même a déjà été filtré.
        if (!currentFolderId) {
          // À la racine, on applique les filtres stricts
          const scopeRaw = String(c.scope || "").toLowerCase();
          // Exclure contenu gratuit/public de la vue "Premium" racine pour éviter les doublons/confusion
          if (c.free === true || scopeRaw === "gratuit" || scopeRaw === "free" || scopeRaw === "public") continue;
          // Filtrage strict par section
          if (!is3AC && SCOPE_3AC_VALUES.includes(String(c.scope))) continue;
        }
        
        map.set(d.id, { id: d.id, ...c });
      }

      const lowerCode  = selected.code.toLowerCase();
      const lowerLabel = selected.label.toLowerCase();
      const arr = Array.from(map.values()).filter((c) => {
        // Si on est dans un dossier spécifique, on fait confiance au dossier (pas de filtre strict sur le sujet)
        if (currentFolderId) return true;

        const s1v = (c.subject || "").toString().toLowerCase();
        const s2v = (c.matiere || "").toString().toLowerCase();
        return (
          s1v === lowerCode || s2v === lowerCode ||
          s1v.includes(lowerCode) || s2v.includes(lowerCode) ||
          s1v === lowerLabel || s2v === lowerLabel ||
          s1v.includes(lowerLabel) || s2v.includes(lowerLabel)
        );
      });

      const getVal = (f) => {
        switch (sortKey) {
          case "name": return (f.name || "").toLowerCase();
          case "size": return typeof f.sizeBytes === "number" ? f.sizeBytes : -1;
          case "createdAt":
          default: return f?.createdAt?.seconds || 0;
        }
      };
      arr.sort((a,b)=>{
        const va = getVal(a), vb = getVal(b);
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
      });

      setContents(arr);
      setLoading(false);
    };

    const unsub = [];
    unsub.push(onSnapshot(q1,  s => { s1  = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (subject)."); setLoading(false); }));
    unsub.push(onSnapshot(q1b, s => { s1b = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (subject/lowercase)."); setLoading(false); }));
    unsub.push(onSnapshot(q2,  s => { s2  = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (matiere)."); setLoading(false); }));
    unsub.push(onSnapshot(q2b, s => { s2b = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (matiere/lowercase)."); setLoading(false); }));
    unsub.push(onSnapshot(q1c, s => { s1c = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (subject/label)."); setLoading(false); }));
    unsub.push(onSnapshot(q1d, s => { s1d = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (subject/label lowercase)."); setLoading(false); }));
    unsub.push(onSnapshot(q2c, s => { s2c = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (matiere/label)."); setLoading(false); }));
    unsub.push(onSnapshot(q2d, s => { s2d = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (matiere/label lowercase)."); setLoading(false); }));
    if (q3)  unsub.push(onSnapshot(q3,  s => { s3  = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (fallback scope 3AC-Fr)."); setLoading(false); }));
    if (q3b) unsub.push(onSnapshot(q3b, s => { s3b = s.docs; mergeAndSet(); }, e => { console.error(e); setError("Erreur (fallback scope variantes)."); setLoading(false); }));
    if (qFolder) {
      console.log("Listening to folder:", currentFolderId);
      unsub.push(onSnapshot(qFolder, s => { 
        console.log("Folder contents found:", s.docs.length);
        sFolder = s.docs; 
        mergeAndSet(); 
      }, e => { 
        console.error(e); 
        setError("Erreur (dossier)."); 
        setLoading(false); 
      }));
    }

    // Listeners supplémentaires pour les synonymes (ex: Mathématiques)
    altNames.forEach((v) => {
      try {
        const qSub = is3AC
          ? query(colContents, where("subject", "==", v), where("scope", "==", "3AC-Fr"))
          : query(colContents, where("subject", "==", v));
        const qMat = is3AC
          ? query(colContents, where("matiere", "==", v), where("scope", "==", "3AC-Fr"))
          : query(colContents, where("matiere", "==", v));
        unsub.push(onSnapshot(qSub, (s) => { s1e.set(v, s.docs); mergeAndSet(); }, (e) => { console.error(e); setError("Erreur (subject/synonyme). "+e.message); setLoading(false); }));
        unsub.push(onSnapshot(qMat, (s) => { s2e.set(v, s.docs); mergeAndSet(); }, (e) => { console.error(e); setError("Erreur (matiere/synonyme). "+e.message); setLoading(false); }));
      } catch (e) {
        console.error("altNames query error", e);
      }
    });
  
    return () => unsub.forEach(u => u && u());
  }, [selected, currentFolderId, teacherUids, blocked, sortKey, sortDir, is3AC]);

  /* ---------- Helpers d'affichage ---------- */
  // titleOf et extOf importés depuis utils/fileUtils

  async function getReadableUrl(c) {
    if (c.r2Key) return r2UrlFromKey(c.r2Key);
    if (c.url) return c.url;
    if (c.fileUrl) return c.fileUrl;
    const path = c.storagePath || c.filePath;
    if (!path) throw new Error("Aucun chemin de fichier");
    const cached = urlCache.current.get(c.id);
    if (cached) return cached;
    const u = await getDownloadURL(ref(storage, path));
    urlCache.current.set(c.id, u);
    return u;
  }

  /* ---------- Fonction pour ouvrir un fichier avec scroll automatique ---------- */
  const openFileWithScroll = async (c) => {
    try {
      const u = await getReadableUrl(c);
      setPreview({ doc: c, url: u });
      setEmbedError(false);
    } catch (e) {
      alert("Impossible d'ouvrir ce fichier : " + e.message);
    }
  };



  /* ---------- Étape 0 — Sauvegarder le niveau ---------- */
  async function onSaveLevel() {
    setLevelSavedMsg("");
    const u = auth.currentUser;
    if (!u) { setLevelSavedMsg("Connecte-toi d'abord."); return; }
    try {
      setSavingLevel(true);
      await setDoc(
        doc(db, "users", u.uid),
        { level: "2bac", levelSavedAt: serverTimestamp() },
        { merge: true }
      );
      setLevelSavedMsg("Niveau enregistré : 2Bac Sc PC/SVT.");
    } catch (e) {
      setLevelSavedMsg(e.message || "Erreur lors de l'enregistrement du niveau.");
    } finally {
      setSavingLevel(false);
    }
  }

  /* ---------- Étape 1 — Code parrainage ---------- */
  async function onConfirmReferral() {
    try {
      setRefAlert(null);
      const raw = refCodeRef.current?.value || "";
      const res = await verifyReferralCode(db, raw);
      if (!res.ok) {
        setRefAlert({ type: "danger", text: res.error || "Code invalide." });
        return;
      }
      const uid = auth.currentUser?.uid;
      if (uid && res.code) {
        await setDoc(
          doc(db, "users", uid),
          {
            referralCode: res.code,
            referrerProfId: res.profId || null,
            referralValidatedAt: serverTimestamp()
          },
          { merge: true }
        );
      }
      setRefAlert({ type: "success", text: res.code ? "Code parrainage validé ! Réduction appliquée (550 DH / an)." : "Validation effectuée. Aucun code appliqué — tarif 600 DH / an." });
    } catch (e) {
      setRefAlert({ type: "danger", text: e.message });
    }
  }

  /* ---------- Étape 2 — Dépôt du reçu VERS R2 ---------- */
  async function onUploadReceipt(e) {
    e.preventDefault();
    setUploadMsg("");
    setUploadPct(0);

    const u = auth.currentUser;
    if (!u) { setUploadMsg("Connecte-toi d'abord."); return; }
    if (!receiptFile) { setUploadMsg("Dépose un justificatif (image ou PDF)."); return; }
    if (!/^(image\/.*|application\/pdf)$/.test(receiptFile.type || "")) {
      setUploadMsg("Format non supporté (image ou PDF)."); return;
    }

    try {
      // 1) déterminer code parrainage éventuel
      const rawInput = refCodeRef.current?.value || "";
      let usedCode = me?.referralCode || (rawInput.trim().toUpperCase() || null);
      let referrerProfId = me?.referrerProfId || null;

      // 2) clé R2 (répertoire "receipts")
      const safeName = sanitizeFileName(receiptFile.name);
      const key = `receipts/${u.uid}/${Date.now()}_${safeName}`;

      // 3) upload direct vers le Worker (petits reçus = upload simple)
      const bearer =
        (typeof process !== "undefined" && process?.env?.REACT_APP_VIDEOS_WORKER_BEARER) ||
        (typeof window !== "undefined" && (window.__WORKER_BEARER || window.REACT_APP_VIDEOS_WORKER_BEARER)) ||
        null;
      const headers = { "Content-Type": receiptFile.type || "application/octet-stream" };
      if (bearer) headers["Authorization"] = `Bearer ${bearer}`;

      const res = await fetch(`${R2_HOST}/upload?key=${encodeURIComponent(key)}`, {
        method: "POST",
        headers,
        body: receiptFile,
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Upload échoué (${res.status}) — ${txt.slice(0,300)}`);
      }

      // 4) créer le doc Firestore "receipts"
      const expectedAmount = usedCode ? priceWithCode : priceFull;
      await addDoc(collection(db, "receipts"), {
        uid: u.uid,
        status: "pending",
        r2Key: key,
        mime: receiptFile.type || null,
        size: receiptFile.size || null,
        referralCode: usedCode || null,
        referrerProfId: referrerProfId || null,
        expectedAmount,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setUploadMsg("Justificatif déposé ! En attente de validation ✅");
      setReceiptFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setUploadMsg(err.message || "Échec du dépôt du justificatif.");
    }
  }

  async function copyRib() {
    try {
      await navigator.clipboard.writeText(SCHOOL_RIB);
      setRibCopied(true);
      setTimeout(() => setRibCopied(false), 2000);
    } catch {
      try {
        const el = document.createElement("input");
        el.value = SCHOOL_RIB;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        setRibCopied(true);
        setTimeout(() => setRibCopied(false), 2000);
      } catch {}
    }
  }

  // Titre dynamique selon la section (?section=3AC-Fr)
  const eleveTitle = is3AC ? "Espace élève 3AC Fr" : "Espace élève 2BAC";
  const subjectsList = is3AC ? SUBJECTS_3AC_FR : SUBJECTS;

  /* ---------- Rendu global ---------- */
  return (
    <>
      {/* Arrière-plan responsive (ne touche pas au contenu) */}
      <div
        className="eleve-bg"
        aria-hidden="true"
        style={{ "--eleve-bg": `url(${eleveBg})` }}
      />
      
      <div
        className="container py-3 eleve-container"
        tabIndex={0}
        onKeyDownCapture={(e)=>{ const k=(e.key||"").toLowerCase(); const ctrl=e.ctrlKey||e.metaKey; const shift=e.shiftKey; if(k==="f12"||(ctrl&&(k==="s"||k==="p"||k==="u"))||(ctrl&&shift&&(k==="i"||k==="j"||k==="c"))){ e.preventDefault(); e.stopPropagation(); } }}
      >
        <h1 className="h5">{eleveTitle}</h1>
        {error && <div className="alert alert-danger my-3">{error}</div>}

        {me?.access === "approved"
          ? (selected ? (
              <>
                {SubjectView()}
                <div className="mt-3">
                  {preview && (
                     <PreviewPanel 
                       key={preview.doc?.id || preview.url} 
                       preview={preview} 
                       isFullscreen={isFullscreen} 
                     />
                  )}
                </div>
              </>
            ) : SubjectsGrid())
          : OnboardingView()
        }
      </div>

      <style>{`
        .eleve-bg{
          position: absolute;                 /* éviter les remontées de page lors de la saisie */
          inset: 0;
          z-index: -1;
          background-image: var(--eleve-bg);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-attachment: scroll;     /* éviter les sauts de scroll sur saisie */
          min-height: 100vh;                  /* couvrir le viewport */
          pointer-events: none;               /* ne pas capter le focus/interactions */
        }
        .eleve-container{                    /* neutralise le scroll anchoring */
          overflow-anchor: none;
        }
        /* Empêche l'auto-zoom iOS et les sauts associés */
        .eleve-container input.form-control,
        .eleve-container input[type="text"],
        .eleve-container input[type="tel"]{
          font-size: 16px;
          line-height: 1.5;
        }
      /* voile léger pour la lisibilité du contenu */
        .eleve-bg::after{
          content:"";
          position:absolute; inset:0;
          background: linear-gradient(0deg, rgba(255,255,255,.60), rgba(255,255,255,.30));
        }
      `}</style>
    </>
  );

  /* ---------- Vues ---------- */
  function OnboardingView() {
    return (
      <div className="mt-3">
        <h2 className="h6">Activation de l'accès</h2>
        <p className="text-muted">
          1) Choisis ton <strong>niveau</strong>. 2) (Optionnel) Saisis un <strong>code parrainage</strong>. 3) Dépose ton <strong>justificatif de paiement</strong>.
          Le code réduit uniquement le montant — l'accès sera activé après validation admin.
        </p>

        {/* Étape 1 — Choix du niveau */}
        <div className="card mb-3">
          <div className="card-body">
            <h3 className="h6 mb-2">Étape 1 — Choix du niveau</h3>

            <div className="d-flex flex-column gap-2" style={{ maxWidth: 520 }}>
              {/* Grisés (tooltip arabe) */}
              <label className="d-flex align-items-center gap-2 text-muted" title="قريبا إن شاء الله" style={{ cursor: "not-allowed", opacity: 0.6 }}>
                <input type="radio" name="level" disabled /> <span>3AC Fr</span>
              </label>
              <label className="d-flex align-items-center gap-2 text-muted" title="قريبا إن شاء الله" style={{ cursor: "not-allowed", opacity: 0.6 }}>
                <input type="radio" name="level" disabled /> <span>TC Sc Fr</span>
              </label>
              <label className="d-flex align-items-center gap-2 text-muted" title="قريبا إن شاء الله" style={{ cursor: "not-allowed", opacity: 0.6 }}>
                <input type="radio" name="level" disabled /> <span>1Bac régional</span>
              </label>

              {/* Seul niveau actif */}
              <label className="d-flex align-items-center gap-2">
                <input type="radio" name="level" checked={levelChoice === "2bac"} onChange={() => setLevelChoice("2bac")} />
                <span className="fw-semibold">2Bac Sc PC/SVT</span>
              </label>

              <div className="d-flex align-items-center gap-2 mt-1">
                <button type="button" className="btn btn-primary btn-sm" onClick={onSaveLevel} disabled={savingLevel}>
                  {savingLevel ? "Enregistrement…" : "Enregistrer le niveau"}
                </button>
                {levelSavedMsg && <span className="text-success small">{levelSavedMsg}</span>}
              </div>

              <div className="form-text">
                Pour le moment, seul le niveau <strong>2Bac</strong> est disponible. Les autres niveaux arriveront <em>قريبا إن شاء الله</em>.
              </div>
            </div>
          </div>
        </div>

        {/* Étape 2 — Code parrainage */}
        <div className="card mb-3">
          <div className="card-body">
            <h3 className="h6 mb-2">Étape 2 — Code parrainage (facultatif)</h3>

            {me?.referralCode ? (
              <div className="alert alert-success py-2 mb-0">
                Code déjà enregistré : <strong>{me.referralCode}</strong> — tarif <strong>550 DH / an</strong>.
              </div>
            ) : (
              <>
                <div className="input-group" style={{ maxWidth: 520 }}>
                  <input
                    className="form-control"
                    placeholder="Ex : AAAA-BBBB-CCCC (exemple)"
                    type="text"
                    ref={refCodeRef}
                  />
                  <button type="button" className="btn btn-outline-primary" onClick={onConfirmReferral}>
                    Confirmer
                  </button>
                </div>

                {refAlert && (
                  <div className={`alert alert-${refAlert.type} mt-2 mb-0 py-2`}>{refAlert.text}</div>
                )}

                <div className="form-text">
                  Un code valide applique uniquement une réduction <strong>(550 DH / an)</strong>. Laisse vide si tu n'en as pas.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Étape 3 — N° de téléphone et consentement */}
        <div className="card mb-3">
          <div className="card-body" style={{ maxWidth: 520 }}>
            <h3 className="h6 mb-2">Étape 3 — N° de téléphone (pour contact)</h3>

            <div className="mb-2">
              <label className="form-label">N° de téléphone</label>
              <input
                className="form-control"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Ex: 0612 345 678"
                ref={phoneInputRef}
              />
              <div className="form-text">Ce numéro sera visible par l'admin uniquement.</div>
            </div>

            <div className="form-check mb-2">
              <input
                id="consentPhone"
                className="form-check-input"
                type="checkbox"
                checked={phoneConsent}
                onChange={(e) => setPhoneConsent(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="consentPhone">
                J'accepte que ce numéro soit associé à mon compte pour contact.
              </label>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={savingPhone}
                onClick={onSavePhone}
              >
                {savingPhone ? "Enregistrement…" : "Enregistrer le téléphone"}
              </button>
              {savePhoneMsg && <span className="small text-success">{savePhoneMsg}</span>}
            </div>
          </div>
        </div>

        {/* Étape 4 — Dépôt du justificatif de paiement */}
        <div className="card">
          <div className="card-body">
            <h3 className="h6 mb-3">Étape 4 — Dépôt du justificatif de paiement</h3>

            {/* Choix du mode de paiement */}
            <div className="mb-3" style={{ maxWidth: 520 }}>
              <label className="form-label fw-semibold">Mode de paiement</label>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="paymentMethod"
                  id="paymentBank"
                  value="bank"
                  defaultChecked
                />
                <label className="form-check-label" htmlFor="paymentBank">
                  Virement bancaire
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="paymentMethod"
                  id="paymentCash"
                  value="cash"
                />
                <label className="form-check-label" htmlFor="paymentCash">
                  Paiement en espèces auprès d'un professeur
                </label>
              </div>
            </div>

            {/* Bloc RIB - visible par défaut */}
            <div className="alert alert-secondary d-flex flex-wrap align-items-center justify-content-between" role="alert" id="ribSection">
              <div className="me-3">
                <div className="fw-semibold">RIB pour le virement</div>
                <div className="fs-5 mt-1" style={{ letterSpacing: ".5px" }}>{SCHOOL_RIB}</div>
                <div className="small text-muted mt-1">
                  Veuillez effectuer le virement sur ce RIB <strong>avant</strong> de déposer le reçu.
                  <span className="ms-1">المرجو القيام بالتحويل إلى هذا الحساب البنكي قبل رفع الوصل.</span>
                </div>
              </div>
              <button type="button" className="btn btn-outline-secondary btn-sm mt-2 mt-md-0" onClick={copyRib}>
                {ribCopied ? "Copié ✓" : "Copier le RIB"}
              </button>
            </div>

            {/* Instructions pour paiement en espèces */}
            <div className="alert alert-info d-none" role="alert" id="cashSection">
              <div className="fw-semibold">Paiement en espèces</div>
              <div className="small mt-1">
                Rendez-vous directement auprès de l'un de nos professeurs partenaires pour effectuer le paiement en espèces.
                Une fois le paiement effectué, déposez la quittance ou le reçu fourni par le professeur.
              </div>
            </div>

            <p className="text-muted mb-2">
              Montant attendu : <strong>{priceToPay} DH</strong> / an
              {hasAppliedCode ? " (réduction appliquée)" : " (sans parrainage)"}.
            </p>

            <form onSubmit={onUploadReceipt} style={{ maxWidth: 520 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="d-none"
                onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
              />
              <div className="input-group">
                <button type="button" className="btn btn-outline-secondary" onClick={() => fileInputRef.current?.click()}>
                  Choisir un fichier
                </button>
                <input type="text" className="form-control" readOnly value={receiptFile ? receiptFile.name : "Aucun fichier n'a été sélectionné"} />
              </div>

              <div className="small mt-1 text-muted">
                Formats acceptés : image ou PDF.
                {receiptFile && <span className="ms-2 text-success">({Math.ceil((receiptFile.size || 0) / 1024)} Ko)</span>}
              </div>

              {/* Barre de progression */}
              {uploading && (
                <div className="progress mt-2" role="progressbar" aria-valuenow={uploadPct} aria-valuemin="0" aria-valuemax="100">
                  <div className="progress-bar" style={{ width: `${uploadPct}%` }}>
                    {uploadPct}%
                  </div>
                </div>
              )}

              <button className="btn btn-primary mt-2" disabled={uploading || !receiptFile} type="submit">
                {uploading ? "Envoi en cours…" : "Déposer le reçu"}
              </button>
            </form>

            {uploadMsg && <div className="alert alert-info mt-3 mb-0">{uploadMsg}</div>}
          </div>
        </div>

        {me?.access === "pending" && (
          <div className="alert alert-warning mt-3">
            Votre accès est <strong>en attente de validation</strong>. Vous serez notifié dès l'activation.
          </div>
        )}
      </div>
    );
  }

  async function onSavePhone() {
    try {
      setSavePhoneMsg("");
      setSavingPhone(true);
      const u = auth.currentUser;
      if (!u) throw new Error("Connecte-toi d'abord.");
      const raw = (phoneInputRef.current?.value || "").replace(/\D+/g, "");
      if (raw.length < 9 || raw.length > 12) throw new Error("Numéro invalide.");
      const pretty = raw.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d*)/, (m, a,b,c,d,e) => [a,b,c,d,e].filter(Boolean).join(" "));
      // Enregistrement via Worker Cloudflare (R2)
      const res = await fetch("https://videos.educapsules.com/phones", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: u.uid,
          displayName: u.displayName || null,
          email: u.email || null,
          phone: pretty,
          consent: !!phoneConsent,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(()=>({}));
        throw new Error(j.error || j.message || "Échec d'enregistrement.");
      }
      setSavePhoneMsg("Téléphone enregistré sur Cloudflare ✅");
    } catch (e) {
      setSavePhoneMsg(e.message || "Échec d'enregistrement.");
    } finally {
      setSavingPhone(false);
    }
  }

  function Breadcrumb() {
    return (
      <div className="d-flex align-items-center gap-1 mb-2" style={{ fontSize: '0.9rem' }}>
        {crumbs.map((c, i) => (
          <span key={c.id || "root"}>
            {i > 0 && <span className="text-muted mx-1">/</span>}
            <button 
              className="btn btn-link p-0" 
              style={{ fontSize: '0.9rem' }}
              onClick={() => { 
                setCurrentFolderId(c.id); 
                setPreview(null); 
                setEmbedError(false); 
              }}
            >
              {c.name}
            </button>
          </span>
        ))}
      </div>
    );
  }

  /* ---------- Composant pour le tri ---------- */
  function SortControls() {
    return (
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="text-muted small" style={{ fontSize: '0.8rem' }}>Trier par:</span>
        <select 
          className="form-select form-select-sm" 
          style={{ width: 'auto', fontSize: '0.8rem' }}
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
        >
          <option value="name">Nom</option>
          <option value="createdAt">Date</option>
          <option value="size">Taille</option>
        </select>
        <button 
          className="btn btn-outline-secondary btn-sm"
          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
          onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          title={sortDir === "asc" ? "Croissant" : "Décroissant"}
        >
          {sortDir === "asc" ? "↑" : "↓"}
        </button>
      </div>
    );
  }

  function FolderGrid() {
    return (
      <>
        {folders.length > 0 && (
          <div className="row g-2 mb-3">
            {folders.map((f) => (
              <div key={f.id} className="col-6 col-sm-4 col-md-3 col-lg-2">
                <button
                  className={`card text-start w-100 ${f.restricted ? 'opacity-50' : ''}`}
                  style={{ 
                    ...compactCardStyle,
                    cursor: 'pointer', 
                    borderColor: f.restricted ? '#6c757d' : undefined,
                    backgroundColor: f.restricted ? '#f8f9fa' : undefined
                  }}
                  onClick={() => {
                    setCurrentFolderId(f.id);
                    setPreview(null);
                    setEmbedError(false);
                  }}
                >
                  <div className="card-body d-flex align-items-center gap-2 p-2">
                    <div style={compactImageStyle}>{f.restricted ? "🔒" : "📁"}</div>
                    <div className="flex-grow-1">
                      <div className={"fw-semibold small " + (f.restricted ? "text-muted" : "")} style={{ fontSize: '0.8rem' }}>
                        {f.name || "Sans nom"}
                      </div>
                      {f.restricted && (
                        <span className="badge bg-secondary" style={{ fontSize: '0.6rem' }}>Inaccessible</span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  function FilesGrid() {
    return (
      <>
        {blocked && <div className="alert alert-danger">{blockedMsg || "Pas pour le moment"}</div>}
        {loading && <p className="text-muted">Chargement…</p>}
        {!loading && contents.length === 0 && <p className="text-muted">Aucun contenu dans ce dossier.</p>}
        {true && (
          <>
            {SortControls()}
            <div className="row g-2">
              {contents.map((c) => (
                <div key={c.id} className="col-6 col-sm-4 col-md-3 col-lg-2">
                  <div className="card h-100" style={compactCardStyle}>
                    <div className="card-body d-flex flex-column p-2">
                      <div className="h6 mb-1 small" style={{ fontSize: '0.8rem', lineHeight: '1.2' }}>
                        {titleOf(c)}
                      </div>
                      <div className="text-muted small mb-2" style={{ fontSize: '0.7rem' }}>
                        {c.description ? (c.description.length > 50 ? c.description.substring(0, 50) + '...' : c.description) : "—"}
                      </div>
                      <div className="mt-auto">
                        <button
                          className="btn btn-primary btn-sm w-100"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          onClick={() => openFileWithScroll(c)}
                        >
                          Voir
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </>
    );
  }


  function SubjectsGrid() {
    return (
      <div className="row g-3 mt-2">
        {subjectsList.map((s) => (
          <div key={s.code} className="col-md-4 col-lg-3">
            <button className="card h-100 shadow-sm text-start w-100" onClick={() => { setSelected(s); setPreview(null); setEmbedError(false); }}>
              <div className="card-body d-flex align-items-center gap-3">
                <div style={{ fontSize: 32 }}>{s.emoji}</div>
                <div>
                  <div className="fw-semibold">{s.label}</div>
                  <div className="text-muted small">{s.code}</div>
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>
    );
  }

  function SubjectView() {
    return (
      <div>
        {isDebug && (
          <div className="alert alert-secondary py-2 px-3 mb-2" style={{ fontSize: '0.8rem' }}>
            <div><strong>Debug:</strong> section={is3AC ? '3AC-Fr' : '2Bac'}; matière={selected?.label} ({selected?.code})</div>
            <div>currentFolderId: {String(currentFolderId || 'root')}</div>
            <div>teacherUids: {teacherUids && teacherUids.length ? teacherUids.join(', ') : '(none)'}</div>
            <div>contents visibles ({contents.length}): {contents && contents.length ? contents.map(c => c.id).slice(0,50).join(', ') : '(none)'}</div>
          </div>
        )}
        <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.9rem' }}>
          <button 
            className="btn btn-outline-secondary btn-sm" 
            style={{ fontSize: '0.8rem' }}
            onClick={() => { setSelected(null); setPreview(null); setEmbedError(false); }}
          >
            ← Matières
          </button>
          <span className="ms-1 text-muted">/</span>
          <span className="ms-1 fw-semibold">{selected.label}</span>
        </div>

        {Breadcrumb()}
        {FolderGrid()}
        {FilesGrid()}

    </div>
  );
}
}

